// Portions of this file are Copyright 2021 Google LLC, and licensed under GPL2+. See COPYING.
import { createInitialState } from "./initial-state.ts";
/**
 * Application state is deliberately NOT round-tripped through the URL.
 *
 * This is a viewer for models served from /libraries, so a link only ever names
 * what to open; it never carries file contents. Previously the whole `params`
 * object (including every open file's text) was gzipped into the fragment and
 * replayed on load, which meant a single failed read got persisted as an empty
 * source and re-broke every subsequent render.
 */
export function buildBlankProjectUrl() {
    return `${location.protocol}//${location.host}${location.pathname}#blank`;
}
export async function readStateFromFragment() {
    if (!window.location.hash.startsWith('#') || window.location.hash.length <= 1) {
        return null;
    }
    const serialized = window.location.hash.substring(1);
    try {
        if (serialized === 'blank') {
            return createInitialState(null, { content: '' });
        }
        else if (serialized.startsWith('src=')) {
            const src = decodeURIComponent(serialized.substring('src='.length));
            return createInitialState(null, { content: src });
        }
        else if (serialized.startsWith('path=')) {
            const path = decodeURIComponent(serialized.substring('path='.length));
            return createInitialState(null, { path });
        }
        else if (serialized.startsWith('url=')) {
            const url = decodeURIComponent(serialized.substring('url='.length));
            const path = '/' + new URL(url).pathname.split('/').pop();
            return createInitialState(null, { path, url });
        }
        console.warn(`Ignoring unrecognized URL fragment: ${serialized.substring(0, 40)}`);
    }
    catch (e) {
        console.error('Failed to read state from URL fragment:', e);
    }
    return null;
}
